// Auto-builder. The settlement places buildings on its own as resources allow.
// Card picks bias which direction it favours, and a small per-level penalty
// spreads construction across building types instead of dumping everything into
// one. A cooldown paces placement so the player watches resources gather and
// then get spent in satisfying chunks, rather than vanishing instantly.

import type { GameState } from '@core/state';
import { BUILDINGS, BUILDING_IDS, buildingCost, type BuildingId } from '@data/buildings';
import type { BuildingAxis } from '@data/buildings';
import { BALANCE } from '@data/balance';
import { bus } from '@core/events';
import { t } from '@i18n/i18n';
import { CARD_BY_ID } from '@data/cards';
import type { ResourceId } from '@data/resources';
import { populationCap } from './stats';

let buildCooldown = 0;

/**
 * Buildings are gated by population so settlement size tracks people (no more
 * eighteen buildings around four citizens). Workplaces need someone to staff
 * them; houses (homes) are built when the population is filling its housing cap,
 * which then raises the cap and lets more people, and more workplaces, arrive.
 */
function passesPopGate(state: GameState, id: BuildingId): boolean {
  const pop = Math.floor(state.run.population);
  if (id === 'house') {
    // Housing is the growth lever: buildable while there are still fewer buildings
    // than people, and also when the cap is full (a new home raises the cap so more
    // people, and then more buildings, can arrive).
    return state.run.buildingCount < pop || state.run.population >= populationCap(state) - 1;
  }
  // Every other building counts toward the population: no more buildings than people.
  return state.run.buildingCount < pop;
}

/** Maps a building's axis to the card direction that favours it. */
function axisToDirection(axis: BuildingAxis): 'economy' | 'military' | 'knowledge' {
  if (axis === 'military') return 'military';
  if (axis === 'knowledge') return 'knowledge';
  return 'economy'; // economy, gather, population all ride the economy direction
}

/** Direction weights derived from the cards picked this run. */
function directionWeights(state: GameState): Record<'economy' | 'military' | 'knowledge', number> {
  const w = { economy: 0, military: 0, knowledge: 0 };
  for (const id of state.run.pickedCards) {
    const card = CARD_BY_ID[id];
    if (card) w[card.category] += 1;
  }
  return w;
}

/** Total cost (after the run's build-cost modifier) for a building's next level. */
function effectiveCost(state: GameState, id: BuildingId): Partial<Record<ResourceId, number>> {
  const raw = buildingCost(id, state.run.buildings[id]);
  const mult = state.run.modifiers.buildCostMult * state.metaPerks.globalBuildCostMult;
  const out: Partial<Record<ResourceId, number>> = {};
  for (const [res, amt] of Object.entries(raw) as [ResourceId, number][]) {
    out[res] = amt * mult;
  }
  return out;
}

function canAfford(state: GameState, cost: Partial<Record<ResourceId, number>>): boolean {
  for (const [res, amt] of Object.entries(cost) as [ResourceId, number][]) {
    if (state.run.resources[res] < amt) return false;
  }
  return true;
}

/** Re-evaluates the settlement visual tier and announces an evolution. */
export function updateSettlementTier(state: GameState): void {
  const tiers = BALANCE.settlementTiers;
  let tier = 0;
  for (let i = 0; i < tiers.length; i++) {
    if (state.run.buildingCount >= tiers[i].at) tier = i;
  }
  if (tier > state.run.settlementTier) {
    state.run.settlementTier = tier;
    bus.emit('settlement:evolve', { tier, name: tiers[tier].name });
    bus.emit('milestone', {
      title: t('milestone.settlementNow', { name: tiers[tier].name }),
      tone: 'epic',
    });
  }
}

export function updateBuildings(state: GameState, dt: number): void {
  buildCooldown -= dt;
  if (buildCooldown > 0) return;

  const weights = directionWeights(state);

  // Score WITHOUT affordability (same ranking as nextPlannedBuilding, which
  // drives the HUD's "next building" bars) and then wait until the target is
  // affordable. The builder saves toward the building the player is watching,
  // so the progress bars only ever move forward. Previously it bought the best
  // AFFORDABLE building, which silently drained the savings the HUD showed.
  let bestId: BuildingId | null = null;
  let bestScore = -Infinity;

  for (const id of BUILDING_IDS) {
    if (!passesPopGate(state, id)) continue;
    const def = BUILDINGS[id];
    const dir = axisToDirection(def.axis);
    const lvl = state.run.buildings[id];
    // Direction bias from cards, spread penalty so types stay roughly balanced.
    const score = def.basePriority * (1 + weights[dir] * 0.5) / (1 + lvl * 0.15);
    if (score > bestScore) {
      bestScore = score;
      bestId = id;
    }
  }

  const bestCost = bestId ? effectiveCost(state, bestId) : null;
  if (bestId && bestCost && !canAfford(state, bestCost)) return;

  if (bestId && bestCost) {
    for (const [res, amt] of Object.entries(bestCost) as [ResourceId, number][]) {
      state.run.resources[res] -= amt;
    }
    state.run.buildings[bestId] += 1;
    state.run.buildingCount += 1;
    buildCooldown = BALANCE.build.minInterval;

    bus.emit('building:built', {
      gx: 0,
      gy: 0,
      building: bestId,
      level: state.run.buildings[bestId],
    });
    updateSettlementTier(state);
  }
}

export function resetBuildCooldown(): void {
  buildCooldown = 0;
}

/**
 * The building the auto-builder is currently aiming for: the highest-priority
 * type that passes the population gate, with its effective cost (affordable or
 * not). Drives the "saving for the next building" bars in the HUD. Returns null
 * when nothing can be built yet (e.g. the population gate blocks everything).
 */
export function nextPlannedBuilding(
  state: GameState,
): { id: BuildingId; cost: Partial<Record<ResourceId, number>>; gatedByPop: boolean } | null {
  const weights = directionWeights(state);
  let bestId: BuildingId | null = null;
  let bestScore = -Infinity;
  for (const id of BUILDING_IDS) {
    if (!passesPopGate(state, id)) continue;
    const def = BUILDINGS[id];
    const dir = axisToDirection(def.axis);
    const lvl = state.run.buildings[id];
    const score = (def.basePriority * (1 + weights[dir] * 0.5)) / (1 + lvl * 0.15);
    if (score > bestScore) {
      bestScore = score;
      bestId = id;
    }
  }
  if (bestId) return { id: bestId, cost: effectiveCost(state, bestId), gatedByPop: false };
  // Nothing passes the population gate: surface the cheapest house as the goal so
  // the HUD can tell the player to grow their population first.
  return { id: 'house', cost: effectiveCost(state, 'house'), gatedByPop: true };
}
